import React from 'react';
import { AllCommunityModule, ColDef, ICellRendererParams, ITooltipParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { SetFilterModule } from 'ag-grid-enterprise';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { ZfIcon, ZfTextButton } from '@zafire/react';
import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/dateUtils';
import { humanReadableString } from '../../utils/stringUtils';

ModuleRegistry.registerModules([AllCommunityModule, SetFilterModule]);

const isoDateFilterComparator = (filterDateAtMidnight: Date, cellValue?: string | null): number => {
  if (!cellValue) return -1;
  const cellDate = parseISO(cellValue);
  if (Number.isNaN(cellDate.getTime())) return -1;
  const cellDayMidnight = new Date(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate());
  if (cellDayMidnight < filterDateAtMidnight) return -1;
  if (cellDayMidnight > filterDateAtMidnight) return 1;
  return 0;
};

export interface LinkedContactNote {
  contactNoteId: string;
  uid?: string;
  startDateTime?: string;
  subject?: string;
  contactType?: string;
  status?: string;
  qualifiedClientContact?: boolean;
}

type UnlinkCellParams = ICellRendererParams<LinkedContactNote> & {
  onUnlink: (contactNoteId: string) => void;
};

const UnlinkCell = ({ data, onUnlink }: UnlinkCellParams) => {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <ZfTextButton
      hierarchy="secondary"
      size="extra-small"
      data-testid={`unlink-contact-note-${data.contactNoteId}`}
      onClick={() => onUnlink(data.contactNoteId)}
    >
      <ZfIcon slot="leading" name="link_off" />
      {t('pb.contactnotes.grid.unlink.label', 'Unlink')}
    </ZfTextButton>
  );
};

const getDefaultColDef = (fitColumnsToWidth: boolean): ColDef<LinkedContactNote> => ({
  sortable: true,
  resizable: true,
  ...(fitColumnsToWidth ? { flex: 1 } : {}),
  suppressHeaderMenuButton: true,
  tooltipValueGetter: ({ valueFormatted, value }: ITooltipParams<LinkedContactNote>) => valueFormatted ?? value,
});

type Props = {
  contactNotes: LinkedContactNote[];
  onUnlink?: (contactNoteId: string) => void;
  showActions?: boolean;
  fitColumnsToWidth?: boolean;
};

export const LinkedContactNotesGrid = React.memo(
  ({ contactNotes, onUnlink, showActions = true, fitColumnsToWidth = true }: Props) => {
    const { t } = useTranslation();
    const columnDefs: ColDef<LinkedContactNote>[] = React.useMemo(
      () => [
        {
          headerName: t('pb.contactnotes.grid.column.id', 'Contact note ID'),
          field: 'contactNoteId',
          filter: 'agTextColumnFilter',
          width: 160,
          ...(fitColumnsToWidth ? {} : { pinned: 'left' as const, lockPinned: true }),
        },
        {
          headerName: t('pb.contactnotes.grid.column.contactdate', 'Contact date'),
          field: 'startDateTime',
          filter: 'agDateColumnFilter',
          filterParams: { comparator: isoDateFilterComparator },
          valueFormatter: ({ value }) => formatDate(value),
          width: 140,
        },
        {
          headerName: t('pb.contactnotes.grid.column.subject', 'Subject'),
          field: 'subject',
          filter: 'agTextColumnFilter',
          width: 240,
        },
        {
          headerName: t('pb.contactnotes.grid.column.communicationchannel', 'Communication channel'),
          field: 'contactType',
          filter: 'agSetColumnFilter',
          filterParams: { valueFormatter: ({ value }: { value: string }) => humanReadableString(value) },
          valueFormatter: ({ value }) => humanReadableString(value),
          width: 200,
        },
        {
          headerName: t('pb.contactnotes.grid.column.status', 'Status'),
          field: 'status',
          filter: 'agSetColumnFilter',
          filterParams: { valueFormatter: ({ value }: { value: string }) => humanReadableString(value) },
          valueFormatter: ({ value }) => humanReadableString(value),
          width: 140,
        },
        {
          headerName: t('pb.contactnotes.grid.column.qualified', 'Qualified'),
          colId: 'qualified',
          filter: 'agSetColumnFilter',
          valueGetter: ({ data }) =>
            data?.qualifiedClientContact
              ? t('pb.contactnotes.grid.qualified.yes', 'Yes')
              : t('pb.contactnotes.grid.qualified.no', 'No'),
          width: 120,
        },
        ...(showActions
          ? [
              {
                headerName: t('pb.contactnotes.grid.column.actions', 'Actions'),
                colId: 'actions',
                sortable: false,
                filter: false,
                flex: 0,
                width: 130,
                cellRenderer: UnlinkCell,
                cellRendererParams: { onUnlink },
                cellStyle: { marginTop: '4px', textAlign: 'right' },
              } as ColDef<LinkedContactNote>,
            ]
          : []),
      ],
      [fitColumnsToWidth, onUnlink, showActions, t],
    );

    const defaultColDef = React.useMemo(() => getDefaultColDef(fitColumnsToWidth), [fitColumnsToWidth]);

    if (!contactNotes.length) return null;

    return (
      <Box
        data-testid="linked-contact-notes-grid"
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          '& .ag-center-cols-viewport, & .ag-center-cols-container': {
            minHeight: '45px !important',
          },
        }}
      >
        <AgGridReact<LinkedContactNote>
          rowData={contactNotes}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          getRowId={(params) => params.data.contactNoteId}
          tooltipShowMode="whenTruncated"
          tooltipShowDelay={300}
          suppressCellFocus
          theme={themeZafire}
        />
      </Box>
    );
  },
);

LinkedContactNotesGrid.displayName = 'LinkedContactNotesGrid';
